/**
 * Hook for fetching and managing watchlist filter options
 * for the Screening IAD alert listing page.
 * Adapted from the Case Manager useWatchlistOptions hook.
 */

import { useState, useRef, useEffect } from 'react';
import { fetchWatchlistFilterOptions } from '../../../../services/ns/investigation';

/**
 * Watchlist filter types with their corresponding API endpoints.
 */
export const WATCHLIST_FILTER_TYPES = [
  {
    key: 'watchlistCategory',
    apiEndpoint: '/ns/iad/onDemandScreening/filters/watchlist-category',
    label: 'Watchlist Category'
  },
  {
    key: 'watchlistSubCategory',
    apiEndpoint: '/ns/iad/onDemandScreening/filters/watchlist-sub-category',
    label: 'Watchlist Sub-category'
  },
  {
    key: 'watchlistKeyword',
    apiEndpoint: '/ns/iad/onDemandScreening/filters/watchlist-keyword',
    label: 'Watchlist Keyword'
  },
  {
    key: 'externalSource',
    apiEndpoint: '/ns/iad/onDemandScreening/filters/external-source',
    label: 'External Source'
  },
  {
    key: 'watchlistSource',
    apiEndpoint: '/ns/iad/onDemandScreening/filters/watchlist-source',
    label: 'Watchlist Source'
  }
];

/**
 * Format watchlist options for dropdown display.
 * Handles case-insensitive deduplication.
 */
const formatWatchlistOptions = (values) => {
  if (!values || !Array.isArray(values)) return [];
  return values.map((value) => ({
    label: value,
    value: value.toLowerCase()
  }));
};

/**
 * Get unique options by merging existing and new options.
 */
const getUniqueWatchlistOptions = (existingOptions, newOptions) => {
  const existingValues = new Set(existingOptions.map((opt) => opt.value));
  const uniqueNewOptions = newOptions.filter(
    (opt) => !existingValues.has(opt.value)
  );
  return [...existingOptions, ...uniqueNewOptions];
};

/**
 * Custom hook for managing watchlist filter options.
 *
 * @param {Array} initialTypes - Array of filter types to manage
 * @param {string} sourceIds - Comma-separated source IDs (e.g., "27,28")
 * @returns {Object} - watchlistOptions, loadingOptions, handleSearch, handlePopupScroll
 */
export const useWatchlistOptions = (
  initialTypes = WATCHLIST_FILTER_TYPES,
  sourceIds = '27,28'
) => {
  const [watchlistOptions, setWatchlistOptions] = useState(
    initialTypes.reduce((acc, { key, apiEndpoint }) => {
      acc[key] = {
        data: [],
        lastSearchTerm: '',
        hasMore: true,
        total: 0,
        searchTotal: 0,
        apiEndpoint
      };
      return acc;
    }, {})
  );

  const [pagination, setPagination] = useState(
    initialTypes.reduce((acc, { key }) => {
      acc[key] = { offset: 0, limit: 100 };
      return acc;
    }, {})
  );

  const [loadingOptions, setLoadingOptions] = useState(
    initialTypes.reduce((acc, { key }) => {
      acc[key] = false;
      return acc;
    }, {})
  );

  // AbortControllers per type
  const controllers = useRef({});

  /**
   * Fetch more options for a specific filter type.
   */
  const fetchMoreOptions = async (key, searchQuery = '', customPagination) => {
    const page = customPagination || pagination[key];

    // Abort previous request
    if (controllers.current[key]) {
      controllers.current[key].abort();
    }

    const controller = new AbortController();
    controllers.current[key] = controller;

    setLoadingOptions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await fetchWatchlistFilterOptions(
        watchlistOptions[key].apiEndpoint,
        {
          offset: page.offset,
          limit: page.limit,
          search: searchQuery,
          sourceIds
        },
        { signal: controller.signal }
      );

      const newOptions = response?.payload?.length
        ? formatWatchlistOptions(response.payload)
        : [];

      const currentData =
        searchQuery && page.offset === 0
          ? newOptions
          : getUniqueWatchlistOptions(watchlistOptions[key].data, newOptions);

      const totalCount = response.total || 0;
      const hasMore = currentData.length < totalCount;

      setWatchlistOptions((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          data: currentData,
          hasMore,
          total: searchQuery ? prev[key].total : totalCount,
          searchTotal: searchQuery ? totalCount : prev[key].searchTotal
        }
      }));
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Fetch for ${key} aborted`);
      } else {
        console.error(`Error fetching ${key}:`, err);
      }
    } finally {
      if (controllers.current[key] === controller) {
        setLoadingOptions((prev) => ({ ...prev, [key]: false }));
        controllers.current[key] = null;
      }
    }
  };

  /**
   * Handle search input for a specific filter type.
   */
  const handleSearch = async (value, key) => {
    const searchTerm = value?.trim() || '';
    const currentOptions = watchlistOptions[key];
    const searchTermChanged = currentOptions.lastSearchTerm !== searchTerm;
    const isSearching = searchTerm !== '';

    // If we already have all the data and not searching, no need to fetch
    if (
      currentOptions.total > 0 &&
      currentOptions.data.length >= currentOptions.total &&
      !isSearching
    ) {
      return;
    }

    if (searchTermChanged) {
      // Reset options for new search
      setWatchlistOptions((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          lastSearchTerm: searchTerm,
          data: [],
          hasMore: true,
          searchTotal: 0
        }
      }));

      // Reset pagination
      const newPagination = {
        ...pagination,
        [key]: { offset: 0, limit: 100 }
      };
      setPagination(newPagination);

      if (!isSearching) {
        await fetchMoreOptions(key, '', newPagination[key]);
        return;
      }
    }

    // Fetch if data is missing or search term changed
    if (searchTermChanged || !currentOptions.data.length) {
      await fetchMoreOptions(key, searchTerm);
    }
  };

  /**
   * Handle popup scroll for infinite loading.
   */
  const handlePopupScroll = async (e, key) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
    const shouldLoadMore =
      isNearBottom && !loadingOptions[key] && watchlistOptions[key]?.hasMore;

    const currentOptions = watchlistOptions[key];

    if (!shouldLoadMore) return;
    if (
      currentOptions.total > 0 &&
      currentOptions.data.length >= currentOptions.total
    ) {
      return;
    }

    const searchTerm = watchlistOptions[key]?.lastSearchTerm || '';
    const newPagination = {
      ...pagination,
      [key]: {
        ...pagination[key],
        offset: pagination[key].offset + pagination[key].limit
      }
    };

    setPagination(newPagination);
    await fetchMoreOptions(key, searchTerm, newPagination[key]);
  };

  /**
   * Initial load of all filter options.
   */
  const initialLoad = async (keys = initialTypes.map((t) => t.key)) => {
    await Promise.all(keys.map((key) => fetchMoreOptions(key)));
  };

  /**
   * Reset all options and reload.
   */
  const resetAndReload = async () => {
    // Reset state
    setWatchlistOptions(
      initialTypes.reduce((acc, { key, apiEndpoint }) => {
        acc[key] = {
          data: [],
          lastSearchTerm: '',
          hasMore: true,
          total: 0,
          searchTotal: 0,
          apiEndpoint
        };
        return acc;
      }, {})
    );

    setPagination(
      initialTypes.reduce((acc, { key }) => {
        acc[key] = { offset: 0, limit: 100 };
        return acc;
      }, {})
    );

    // Reload
    await initialLoad();
  };

  // Auto-load on mount
  useEffect(() => {
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceIds]);

  return {
    watchlistOptions,
    loadingOptions,
    handleSearch,
    handlePopupScroll,
    resetAndReload
  };
};

export default useWatchlistOptions;
